export interface ValidationRule {
  field: string;
  rules: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    email?: boolean;
    phone?: boolean;
  };
  message?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: { [field: string]: string };
}

export const validateAge = (dateOfBirth: string, minimumAge: number = 18, t?: any): string | null => {
  if (!dateOfBirth || dateOfBirth.trim().length === 0) {
    return t ? t('form.validation.dateRequired') : 'Date of birth is required';
  }

  // Check if the date format is valid (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateOfBirth)) {
    return t ? t('form.validation.dateFormat') : 'Please enter a valid date in YYYY-MM-DD format';
  }

  const birthDate = new Date(dateOfBirth);
  
  // Check if it's a valid date
  if (isNaN(birthDate.getTime())) {
    return t ? t('form.validation.dateInvalid') : 'Please enter a valid date';
  }

  // Check if the date is in the future
  const today = new Date();
  if (birthDate > today) {
    return t ? t('form.validation.dateFuture') : 'Date of birth cannot be in the future';
  }

  // Calculate age
  const ageInMilliseconds = today.getTime() - birthDate.getTime();
  const ageInYears = Math.floor(ageInMilliseconds / (1000 * 60 * 60 * 24 * 365.25));

  if (ageInYears < minimumAge) {
    return t ? t('form.validation.ageMinimum').replace('{age}', minimumAge.toString()) : `You must be at least ${minimumAge} years old`;
  }

  return null;
};

export const validateField = (value: string, rules: ValidationRule['rules']): string | null => {
  if (rules.required && (!value || value.trim().length === 0)) {
    return 'This field is required';
  }

  if (value && rules.minLength && value.length < rules.minLength) {
    return `Minimum length is ${rules.minLength} characters`;
  }

  if (value && rules.maxLength && value.length > rules.maxLength) {
    return `Maximum length is ${rules.maxLength} characters`;
  }

  if (value && rules.pattern && !rules.pattern.test(value)) {
    return 'Invalid format';
  }

  if (value && rules.email && !value.includes('@')) {
    return 'Invalid email format';
  }

  if (value && rules.phone && value.replace(/\D/g, '').length < 10) {
    return 'Invalid phone number';
  }

  return null;
};

export const validateForm = (
  formData: { [key: string]: string },
  validationRules: ValidationRule[]
): ValidationResult => {
  const errors: { [field: string]: string } = {};

  validationRules.forEach(rule => {
    const value = formData[rule.field] || '';
    const error = validateField(value, rule.rules);
    
    if (error) {
      errors[rule.field] = rule.message || error;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Predefined validation rules for common form types
export const CONTACT_FORM_RULES: ValidationRule[] = [
  {
    field: 'name',
    rules: { required: true, minLength: 2 },
    message: 'Name is required and must be at least 2 characters'
  },
  {
    field: 'phone',
    rules: { phone: true }, // Optional, but must be valid if provided
    message: 'Valid phone number is required if provided'
  },
  {
    field: 'email',
    rules: { email: true }, // Optional, but must be valid if provided
    message: 'Valid email address is required if provided'
  }
];

export const CARFAX_FORM_RULES: ValidationRule[] = [
  {
    field: 'name',
    rules: { required: true, minLength: 2 },
    message: 'Name is required'
  },
  {
    field: 'phone',
    rules: { phone: true }, // Optional, but must be valid if provided
    message: 'Valid phone number is required if provided'
  }
];

export const OFFER_FORM_RULES: ValidationRule[] = [
  {
    field: 'name',
    rules: { required: true, minLength: 2 },
    message: 'Name is required'
  },
  {
    field: 'phone',
    rules: { phone: true }, // Optional, but must be valid if provided
    message: 'Valid phone number is required if provided'
  }
];

// Step validation functions for multi-step forms
export const validateContactFormStep = (step: number, formData: { [key: string]: string }): boolean => {
  switch (step) {
    case 0:
      // Name is required, and at least phone OR email must be provided
      const hasName = !!formData.name?.trim();
      const hasPhone = !!formData.phone?.trim();
      const hasEmail = !!formData.email?.trim();
      return hasName && (hasPhone || hasEmail);
    case 1:
      return true; // Email is optional
    case 2:
      return true; // Question field is optional
    default:
      return false;
  }
};